import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { CallState, LoadingState } from "@app/root-store/api-result.state";
import { ClareSort, TableActionOptions, TableFilterProperties, TablePageProperties } from "@app/shared/model/action.model";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";

import { PageEvent } from "@angular/material/paginator";
import { SortDirection } from "@angular/material/sort";
import { StatusCount } from "@app/project/store/projects.state";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { TablePropertiesService } from "@app/shared/services/table-properties.service";
import { ComponentFactoryConfig } from "../clare-custom-table/clare-custom-table.component";

@Component({
    selector: "clare-list-view",
    templateUrl: "./clare-list-view.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClareListViewComponent<T extends { id: string | number } & Record<string, any>> implements OnInit, OnChanges {
    @Input() title: string;
    @Input() addButtonText: string;
    @Input() columns: any;
    @Input() useAlternateAdd = false;

    @Input() source: T[];
    @Input() callState: CallState<any>;

    @Input() statusCounts: StatusCount[];
    @Input() activeCounts: StatusCount[];
    @Input() hideHeader: boolean;
    @Input() hideSearch: boolean;
    @Input() isMobileView: boolean;
    @Input() hidePagination: boolean;
    @Input() hideRefresh: boolean;
    @Input() isAccountListView = false;
    @Input() hidePageSize = false;
    @Input() placeholder = "Search";
    @Input() filterPosition: "header" | "table" | "none" = "header";
    @Input() tableClass = "";
    @Input() isSearchSuffixIconShown = false;
    @Input() shouldShowBlankCellDash = false;

    // Optional
    @Input() pageSize = 20;
    @Input() sortActive: string = null;
    @Input() initialQueryString = "";
    @Input() initialStatusFilters: { [key: string]: string[] } = {};

    @Input() tableHint: string;
    @Input() displayItemCount = true;
    @Input() enableAddButton = true;
    @Input() tableActionOptions: TableActionOptions;
    @Input() placeSearchLeft = false;

    @Input() factoryConfig: ComponentFactoryConfig;
    @Input() exportCSVFactoryConfig: ComponentFactoryConfig;

    @Output() onAdd = new EventEmitter<boolean>();
    @Output() onEdit = new EventEmitter<T>();
    @Output() onDeleteItem = new EventEmitter<T>();
    @Output() onRefresh = new EventEmitter<void>();
    @Output() onFilter = new EventEmitter<Partial<TableFilterProperties>>();

    // just need something that will trigger a digest cycle in the table component
    public selectedRow: T;
    public sortDirection: SortDirection = "asc";
    public pagedSource$: Observable<T[][]>;
    public isLoading = true;

    private tableProps$ = new BehaviorSubject<Partial<TablePageProperties>>({
        pageSize: this.pageSize,
        active: this.sortActive,
        direction: this.sortDirection,
        dataSortType: "default",
    });

    ngOnInit(): void {
        if (!this.onAdd?.observers.length) {
            this.enableAddButton = false;
        }

        if (!this.onRefresh.observers.length) {
            this.hideRefresh = true;
        }

        this.setPagedSource();
    }

    ngOnChanges(changes: NgChanges<ClareListViewComponent<T>>) {
        if (changes.pageSize || changes.sortActive) {
            this.tableProps$.next({ pageSize: this.pageSize, active: this.sortActive, direction: this.sortDirection });
        }

        if (changes.source) {
            this.onSelectionChange(null);
            this.setPagedSource();
        }

        if (changes.callState) {
            this.isLoading = this.callState !== LoadingState.loaded;
        }
    }

    /**
     * @description Breaks the source data into pages based on the current pagination properties
     */
    setPagedSource() {
        this.pagedSource$ = this.tableProps$.pipe(
            tap(_ => {
                if (this.selectedRow) {
                    this.selectedRow = this.source?.find(d => d.id === this.selectedRow.id);
                }
            }),
            map(props => {
                const sortedData = TablePropertiesService.sortData(this.source || [], props);
                return TablePropertiesService.paginateData(sortedData, props);
            })
        );
    }

    /**
     * @description Emits an event to the parent component to update the data source based on the new filter info
     * @param {Partial<TableFilterProperties>} $event the updated table properties
     */
    public onFilterChange($event: Partial<TableFilterProperties>) {
        this.onFilter.emit($event);
    }

    /**
     * @description Updates the tableProps$ subject with the updated pagination properties
     * @param {PageEvent} $event the page event
     */
    public onPageChange($event: PageEvent) {
        if ($event.pageSize && this.isAccountListView) {
            window.localStorage.setItem("cachePageSize", $event.pageSize.toString());
        }
        this.tableProps$.next({ ...this.tableProps$.value, ...$event });
    }

    /**
     * @description Updates the tableProps$ subject with the updated sort properties
     * @param {ClareSort} $event the sort event
     */
    public onSortChange($event: ClareSort) {
        this.tableProps$.next({ ...this.tableProps$.value, ...$event });
    }

    /**
     * @description Emits an event to be handled by the parent component when the refresh button is clicked
     */
    onDataRefresh() {
        this.onRefresh.emit(null);
    }

    /**
     * @description Emits an event to be handled by the parent component when an item is to be added
     */
    onAddClick() {
        this.onAdd.emit(true);
    }

    /**
     * @description Emits an event to be handled by the parent component when an item is to be edited
     * @param {*} $event the row item to be edited
     */
    onEditClick($event: T) {
        this.onEdit.emit($event);
    }

    /**
     * @description Sets the currently selected row in the table
     * @param {*} $event the selected row item
     */
    onSelectionChange($event: T) {
        this.selectedRow = $event;
    }
}
